/** Inline click-to-edit description field — blur/Enter saves, Esc cancels */

import { useState, useRef, useEffect, useCallback } from 'react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'

const MAX_LENGTH = 1000
const COUNTER_THRESHOLD = 100 // show counter when within this many chars of limit

interface DescriptionEditorProps {
  value: string | null
  /** Ignored: colours come from theme tokens. Kept so legacy callers still compile. */
  isDark?: boolean
  onSave: (description: string | null) => void
  /** True while save mutation is in-flight */
  saving?: boolean
}

export function DescriptionEditor({ value, onSave, saving }: DescriptionEditorProps) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  // Return focus to the trigger only after a keyboard save/cancel, not when the user clicked elsewhere
  const refocusTrigger = useRef(false)

  // Sync external value changes (e.g. optimistic rollback)
  useEffect(() => {
    if (!editing) setDraft(value ?? '')
  }, [value, editing])

  // Auto-focus + resize on enter edit; back to the trigger after a keyboard exit
  useEffect(() => {
    if (editing && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      el.selectionStart = el.selectionEnd = el.value.length
      autoResize(el)
    } else if (!editing && refocusTrigger.current) {
      refocusTrigger.current = false
      triggerRef.current?.focus()
    }
  }, [editing])

  function autoResize(el: HTMLTextAreaElement) {
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  const handleSave = useCallback(() => {
    const trimmed = draft.trim()
    const next = trimmed.length === 0 ? null : trimmed
    // Only save if changed
    if (next !== (value ?? null)) onSave(next)
    setEditing(false)
  }, [draft, value, onSave])

  const handleCancel = useCallback(() => {
    setDraft(value ?? '')
    setEditing(false)
  }, [value])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      refocusTrigger.current = true
      handleSave()
    } else if (e.key === 'Escape') {
      // Keep Escape from also closing an enclosing dialog (its listener sits on document)
      e.stopPropagation()
      refocusTrigger.current = true
      handleCancel()
    }
  }

  const charsLeft = MAX_LENGTH - draft.length
  const showCounter = charsLeft <= COUNTER_THRESHOLD

  if (editing) {
    return (
      <div className="px-3 pb-3">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value.slice(0, MAX_LENGTH)); autoResize(e.target) }}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          rows={2}
          maxLength={MAX_LENGTH}
          aria-label={t('storage.description.label')}
          className="w-full resize-none rounded-md border border-accent bg-surface px-2 py-1 text-xs leading-relaxed text-fg outline-none focus:ring-1 focus:ring-accent"
        />
        {showCounter && (
          <p className={cn('mt-0.5 text-right text-xs', charsLeft <= 20 ? 'text-danger' : 'text-fg-subtle')}>
            {t('storage.description.charsLeft', { count: charsLeft })}
          </p>
        )}
      </div>
    )
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={() => setEditing(true)}
      disabled={saving}
      aria-label={value ? t('storage.description.edit') : t('storage.description.add')}
      title={value ? t('storage.description.edit') : undefined}
      className={cn(
        'w-full cursor-pointer px-3 pb-3 text-left text-xs leading-relaxed transition-colors hover:text-fg disabled:cursor-wait',
        value ? 'text-fg-muted' : 'text-fg-subtle',
      )}
    >
      {value ?? t('storage.description.placeholder')}
    </button>
  )
}
